import { motion } from "framer-motion";

const CHAPTERS = [
  {
    number: "01",
    title: "Exterior Hand Wash & Decon",
    desc: "Two-bucket foam hand wash, wheel & tire deep clean, bug and tar removal, and streak-free glass.",
    badge: "Exterior Phase",
    image: "/media/work-exterior.jpg",
  },
  {
    number: "02",
    title: "Interior Deep Restoration",
    desc: "Full vacuum, steam cleaning, leather clean & condition, and every vent, seam and cupholder detailed by hand.",
    badge: "Interior Phase",
    image: "/media/work-interior.jpg",
    portrait: true,
  },
  {
    number: "03",
    title: "The Full Detail",
    desc: "Exterior and interior combined in a single visit — every panel, seat and stitch refreshed to like-new.",
    badge: "Signature Phase",
    image: "/media/work-full.jpg",
  },
  {
    number: "04",
    title: "Finishing Touches",
    desc: "Spray sealant, tire shine, a light fragrance mist, and a final walk-around inspection together with you.",
    badge: "Delivery Phase",
    image: "/media/work-seats.jpg",
  },
];

const reveal = {
  hidden: { opacity: 0, y: 48 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export default function Manifesto() {
  return (
    <section id="manifesto" data-testid="manifesto-section" className="relative py-20 sm:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="mb-16 max-w-3xl sm:mb-24"
        >
          <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-[#FFB703]">01 // The Manifesto</p>
          <h2 className="font-display text-[clamp(1.4rem,6vw,2.25rem)] font-extrabold uppercase tracking-tight text-white sm:text-4xl lg:text-5xl">
            Chapters of <span className="text-[#FFB703]">Perfection</span>
          </h2>
          <p className="mt-5 text-sm leading-relaxed text-slate-400 sm:text-base">
            A four-step relentless methodology, engineered for uncompromising collectors and daily-driven supercars alike.
          </p>
        </motion.div>

        <div className="flex flex-col gap-20 sm:gap-28">
          {CHAPTERS.map((c, i) => (
            <motion.article
              key={c.number}
              data-testid={`manifesto-chapter-${c.number}`}
              variants={reveal}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-100px" }}
              className={`grid items-center gap-8 lg:grid-cols-2 lg:gap-16 ${i % 2 === 1 ? "" : ""}`}
            >
              <div className={`${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className={`group relative overflow-hidden rounded-2xl border border-white/10 shadow-[0_0_60px_rgba(0,0,0,0.6)] ${c.portrait ? "mx-auto max-w-md" : ""}`}>
                  <img
                    src={c.image}
                    alt={c.title}
                    loading="lazy"
                    className={`w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 ${c.portrait ? "aspect-[4/5]" : "aspect-[16/10]"}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#090A0E]/70 via-transparent to-transparent" />
                  <span className="absolute bottom-4 left-4 rounded-full border border-white/15 bg-[#090A0E]/70 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFB703] backdrop-blur-md">
                    {c.badge}
                  </span>
                </div>
              </div>
              <div className={`${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <p className="font-mono text-6xl font-bold text-white/[0.07] sm:text-8xl">{c.number}</p>
                <h3 className="-mt-6 font-display text-xl font-bold tracking-tight text-white sm:-mt-10 sm:text-2xl lg:text-3xl">
                  {c.title}
                </h3>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-400 sm:text-base">{c.desc}</p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
